<template>
  <div :class="['buttonList', { 'buttonList--hero': variant === 'hero' }]" v-if="!hideButtons">
  <button :class="['contact-button', { 'hero-quote-button': variant === 'hero' }]" @click="openModal()">Request a free quote</button>
  <button :class="['financing-button', { 'hero-financing-button': variant === 'hero' }]" @click="openFinancingLink()" aria-label="Apply for financing options">Apply for financing</button>
  </div>
  <div ref="modal" class="modal">
    <div class="modal-content"  data-scroll-index="7">
      <span class="close" @click="hardCloseModal()">&times;</span>
      

      <div class="contact-us-title" v-if="!isSubmitted || errorMessage">
        <h2>
          <span v-if="!isSubmitted">Get Your Free Quote</span>
          <span v-else>We couldn't send your request</span>
        </h2>
        <p v-if="isSubmitted && errorMessage" class="error-fallback">
          Sorry, your request didn't go through. Please call or email us directly and we'll get your free quote started:
          <br><br>
          📞 <a v-if="isGaylord" :href="officeTel">989-858-3559</a><a v-else href="#" @click.prevent="showPhoneModal = true">269-262-5055</a>
          <br>
          ✉️ <a :href="fallbackMailto">Email us your request</a> (already filled in)
        </p>
      </div>

      <!-- Shown after a request goes through. -->
      <section v-if="isSubmitted && !errorMessage" class="thanks" aria-live="polite">
        <div class="thanks-head">
          <span class="thanks-check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="30" height="30" focusable="false"><path fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" d="M5 12.5l4.5 4.5L19 7.5"/></svg>
          </span>
          <h2 v-if="!wasDuplicate">Thanks{{ firstName ? ', ' + firstName : '' }}! We got your request.</h2>
          <h2 v-else>We already have your request</h2>
          <p v-if="!wasDuplicate" class="thanks-lead">
            Our {{ officeLabel }} office will {{ contactByEmail ? 'reach out' : 'call you' }} within 24 hours to set up your free inspection and estimate.
          </p>
          <p v-else class="thanks-lead">
            It looks like this one already came through, so there's no need to send it again. Our {{ officeLabel }} office will be in touch within 24 hours.
          </p>
          <p v-if="confirmed && email" class="thanks-note">We also emailed a confirmation to <strong>{{ email }}</strong>.</p>
        </div>

        <div v-if="isStorm" class="thanks-tip">
          <strong>Storm damage?</strong> If water is coming in right now, call us at
          <a :href="officePhoneHref">{{ officePhoneDisplay }}</a> instead of waiting.
          If it's safe, take photos of the damage from the ground before anything is cleaned up. They help with an insurance claim.
        </div>

        <div class="thanks-card">
          <h3>What you sent</h3>
          <dl class="thanks-summary">
            <div v-if="services"><dt>Service</dt><dd>{{ services }}</dd></div>
            <div><dt>Phone</dt><dd>{{ phone }}</dd></div>
            <div v-if="locationLine"><dt>Location</dt><dd>{{ locationLine }}</dd></div>
            <div v-if="message"><dt>Details</dt><dd class="thanks-details">{{ message }}</dd></div>
          </dl>
          <p class="thanks-fix">Something wrong? Call <a :href="officePhoneHref">{{ officePhoneDisplay }}</a> and we'll fix it.</p>
        </div>

        <div class="thanks-card">
          <h3>What happens next</h3>
          <ol class="thanks-steps">
            <li><strong>We {{ contactByEmail ? 'get in touch' : 'call you' }}.</strong> We'll set up a time that works for you.</li>
            <li><strong>Free inspection and estimate.</strong> We look over your roof and give you a clear quote. It costs nothing and you are never obligated.</li>
            <li><strong>We do the work.</strong> Our licensed, insured crew does the job with quality materials and cleans up when we finish.</li>
          </ol>
        </div>

        <div class="thanks-actions">
          <a class="thanks-btn thanks-btn--primary" :href="officePhoneHref">Call {{ officePhoneDisplay }}</a>
          <router-link class="thanks-btn" to="/roofing-projects" @click="hardCloseModal()">See our recent roofs</router-link>
          <button type="button" class="thanks-btn" @click="openFinancingLink()">Financing options</button>
        </div>
        <button type="button" class="thanks-close" @click="hardCloseModal()">Close</button>
      </section>


      <form v-if="!isSubmitted" class="form-data-collection" ref="form" @submit.prevent="submitForm">
        <input v-model="website" type="text" name="website" class="hp-field" tabindex="-1" autocomplete="off" aria-hidden="true" />
        <div class="form-groups">
          <div class="form-group">
            <input type="text" :id="fid('name')" name="name" v-model="name" autocomplete="name" placeholder=" " required>
            <label :for="fid('name')" class="floating-label">Name</label>
          </div>

          <div class="form-group">
            <input type="tel" :id="fid('phone')" name="phone" v-model="phone" autocomplete="tel" placeholder=" " required>
            <label :for="fid('phone')" class="floating-label">Phone</label>
          </div>

          <div class="form-group">
            <input type="text" :id="fid('city')" name="city" v-model="city" autocomplete="address-level2" placeholder=" " required>
            <label :for="fid('city')" class="floating-label">City</label>
          </div>

          <div class="form-group">
            <input type="text" :id="fid('zip')" name="zip-code" v-model="zipCode" autocomplete="postal-code" inputmode="numeric" placeholder=" " pattern="[0-9]{5}(-[0-9]{4})?" maxlength="10" title="5-digit zip code, like 49120 or 49120-1234">
            <label :for="fid('zip')" class="floating-label">Zip (optional)</label>
          </div>

          <div class="form-group">
            <input type="text" :id="fid('address')" name="address" v-model="address" autocomplete="street-address" placeholder=" ">
            <label :for="fid('address')" class="floating-label">Street address (optional)</label>
          </div>

          <div class="form-group">
            <input type="email" :id="fid('email')" name="email" v-model="email" autocomplete="email" placeholder=" ">
            <label :for="fid('email')" class="floating-label">Email (optional)</label>
          </div>
        </div>
        <div class="Selections">
          <label class="selector-label" :for="fid('service')">What services are you interested in?</label>
          <select v-model="services" :id="fid('service')" name="services" class="form-select">
            <option value="" disabled selected>Select one</option>
            <option value="Roof Replacement">Roof Replacement</option>
            <option value="Roof Repair">Roof Repair</option>
            <option value="Storm Damage / Insurance Claim">Storm Damage / Insurance Claim</option>
            <option value="Siding">Siding</option>
            <option value="Fascia & Soffit">Fascia & Soffit</option>
            <option value="Inspections">Inspections</option>
            <option value="Gutter Replacement">Gutter Replacement</option>
            <option value="Winter Services">Winter Services</option>
            <option value="Other">Other</option>
          </select>

          <label class="selector-label" :for="fid('message')">Tell us about the project <span class="optional">(optional)</span></label>
          <textarea v-model="message" :id="fid('message')" name="message" class="form-textarea" rows="3" maxlength="2000"
            placeholder="Example: Leak over the kitchen after the last storm, a few shingles missing."></textarea>

          <label class="selector-label" :for="fid('contact')">How would you like to be contacted?</label>
          <select v-model="contactInfo" :id="fid('contact')" name="contact-preference" class="form-select">
            <option value="" disabled selected>Select one</option>
            <option value="Phone Call">Phone Call</option>
            <option value="Email">Email</option>
          </select>

          <label class="selector-label" :for="fid('financing')">Are you interested in financing?</label>
          <select v-model="financing" :id="fid('financing')" name="financing" class="form-select">
            <option value="" disabled selected>Select one</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Not Sure">I Would Like More Information</option>
          </select>

          <label class="selector-label" :for="fid('heard')">How did you hear about us?</label>
          <select v-model="heardAbout" :id="fid('heard')" name="heard-about" class="form-select">
            <option value="" disabled selected>Select one</option>
            <option value="Google search">Google search</option>
            <option value="Google Maps">Google Maps</option>
            <option value="Facebook">Facebook</option>
            <option value="Friend or family">Friend or family</option>
            <option value="Laugh Lounge">Laugh Lounge</option>
            <option value="Yard sign or truck">Yard sign or truck</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <button type="submit" class="Request" :disabled="isSending">{{ isSending ? 'Sending…' : 'Request Quote' }}</button>
      </form>

    <div v-if="!isSubmitted" class="bottom-content">
        <div class="container">
            <div class="row">
                <div class="col-lg-6 d-flex align-items-center">
                    <div class="contact-info">

                        <h2 class="contact-title">Prefer to Contact Us Directly?</h2>
                        <ul class="contact-info">
                            <li>
                              <div class="info-left">
                                  <i class="fas fa-mobile-alt"></i>
                              </div>
                              <div class="info-right">

                                <p>Phone: <a v-if="isGaylord" :href="officeTel">989-858-3559</a><a v-else href="#" @click.prevent="showPhoneModal = true">269-262-5055</a></p>
                              </div>
                            </li>
                            <li>
                              <div class="info-left">
                                  <i class="fas fa-at"></i>
                              </div>
                              <div class="info-right">
                                <p>Email: <a href="mailto:homesroofingco@gmail.com">homesroofingco@gmail.com</a></p>
                              </div>
                            </li>
                            <li>
                            </li>
                        </ul>
                    </div>
                </div>
          </div>
        </div>  
      </div>  
    </div>
  </div>
  <PhoneModal v-if="showPhoneModal" @close="showPhoneModal = false" />
</template>

<script>

import { trackEvent } from '@/analytics';
import PhoneModal from '@/components/PhoneModal.vue';
import { getLeadSource } from '@/leadSource';

/* eslint-disable */
// The page can hold several copies of this form (menu, hero, floating button), so each copy
// numbers its field ids to keep every label tied to its own box.
let formCount = 0;

export default {
  name: 'ContactForm',
  components: { PhoneModal },
  props: {
    hideButtons: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'default'
    },
    modal: {
      type: Boolean,
      default: true
    },
    presetCity: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      errorMessage: '',
      name: '',
      phone: '',
      address: '',
      city: '',
      zipCode: '',
      email: '',
      contactInfo: '',
      financing: '',
      services: '',
      heardAbout: '',
      message: '',
      website: '',
      openedAt: 0,
      modalOpen: false,
      isSubmitted: false,
      isSending: false,
      showPhoneModal: false,
      formId: ++formCount,
      confirmed: false,
      office: '',
      wasDuplicate: false,
    }
  },
  mounted() {
    this.openedAt = Date.now();
    this.showPreview();
  },
  methods: {
    // Preview the screens that appear after sending, without sending anything:
    //   /?preview=thanks                   Niles thank-you screen (sample details)
    //   /gaylord-roof-repair?preview=thanks Gaylord version
    //   add &storm for the storm-damage tip, &email for the confirmation line,
    //   or use ?preview=repeat / ?preview=error for those screens.
    // Nothing is emailed, alerted or logged. Only the hero form on the page opens.
    showPreview() {
      if (this.variant !== 'hero' || typeof window === 'undefined') return;
      const q = new URLSearchParams(window.location.search);
      const mode = q.get('preview');
      if (!['thanks', 'repeat', 'error'].includes(mode)) return;
      this.name = 'Sample Customer';
      this.phone = '(000) 000-0000';
      this.city = this.isGaylord ? 'Gaylord' : 'Niles';
      this.message = 'This is a preview. Nothing was sent.';
      if (q.has('storm')) this.services = 'Storm Damage / Insurance Claim';
      if (q.has('email')) { this.email = 'sample@example.com'; this.confirmed = true; }
      this.office = this.isGaylord ? 'Gaylord' : 'Niles';
      this.wasDuplicate = mode === 'repeat';
      this.errorMessage = mode === 'error';
      this.isSubmitted = true;
      this.$nextTick(() => this.openModal());
    },
    fid(field) {
      return 'qf' + this.formId + '-' + field;
    },
    openFinancingLink() {
      trackEvent('financing_click');
      window.open("https://www.enhancify.com/homes-roofing", "_blank", "noopener");
    },
    openModal() {
      // This component's own popup: the page can hold several copies of the form (menu,
      // floating button, hero), and each must open the one with its own location.
      this.$refs.modal.style.display = 'flex';
      this.modalOpen = true
      document.body.classList.add('modal-open')
    },
    hardCloseModal() {
      this.$refs.modal.style.display = 'none';
      this.modalOpen = false
      document.body.classList.remove('modal-open')
    },
    closeModal() {
      this.isSubmitted = true;
      document.body.classList.remove('modal-open')
    },
    submitForm() {
      const params = {
        name: this.name,
        phone: this.phone,
        address: this.address,
        city: this.city,
        zipCode: this.zipCode,
        email: this.email,
        contactInfo: this.contactInfo,
        financing: this.financing,
        services: this.services,
        message: this.message,
        sourceLocation: this.presetCity || 'General',
        heardAbout: this.heardAbout || 'Not answered',
        leadSource: getLeadSource().source,
        landingPage: getLeadSource().landing,
        // Bot checks: the hidden field, and how long the page was open before sending.
        website: this.website,
        elapsed: Date.now() - this.openedAt
      }

      // /lead.php (public/lead.php) emails the office, alerts Brett's phone, confirms to the
      // customer and logs the request. It quietly drops bots and spam before any of that.
      // The customer sees "sent" only if the office email or the phone alert got through.
      this.isSending = true;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      fetch('/lead.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
        signal: controller.signal
      })
        .then(res => res.json())
        .catch(() => ({ success: false }))
        .then(result => {
          clearTimeout(timer);
          if (result.success) {
            if (!this.website) trackEvent('generate_lead', { form: 'quote_request', location: this.presetCity || 'General' });
            this.errorMessage = '';
            this.confirmed = !!result.confirmed;
            this.office = result.office || '';
            this.wasDuplicate = !!result.duplicate;
            if (this.$refs.form) this.$refs.form.reset();
          } else {
            console.log('Quote request failed', result.message || '');
            this.errorMessage = true;
          }
          this.isSending = false;
          this.isSubmitted = true;
          document.body.classList.remove('modal-open');
        });
    }
  },
  computed: {
    // Forms on the Gaylord pages show the Gaylord office number; the rest keep the
    // Niles number, which opens the two-office chooser.
    isGaylord() {
      return this.presetCity === 'Gaylord';
    },
    officeTel() {
      return this.isGaylord ? 'tel:+19898583559' : 'tel:+12692625055';
    },
    // After a request goes through, the server says which office it went to (a Gaylord-area
    // town typed on a Niles page still goes to Gaylord).
    officeLabel() {
      return this.office || (this.isGaylord ? 'Gaylord' : 'Niles');
    },
    officePhoneDisplay() {
      return this.officeLabel === 'Gaylord' ? '(989) 858-3559' : '(269) 262-5055';
    },
    officePhoneHref() {
      return this.officeLabel === 'Gaylord' ? 'tel:+19898583559' : 'tel:+12692625055';
    },
    firstName() {
      const first = (this.name || '').trim().split(/\s+/)[0] || '';
      return /^[A-Za-z][A-Za-z'\-]{0,29}$/.test(first) ? first : '';
    },
    contactByEmail() {
      return this.contactInfo === 'Email' && !!this.email;
    },
    isStorm() {
      return this.services === 'Storm Damage / Insurance Claim';
    },
    locationLine() {
      return [this.address, this.city, this.zipCode].filter(Boolean).join(', ');
    },
    // If nothing went through, a one-tap email with their request already written out.
    fallbackMailto() {
      const lines = [
        'Hi, I tried to request a free quote on your website but it did not go through.',
        '',
        'Name: ' + this.name,
        'Phone: ' + this.phone,
        'Address: ' + [this.address, this.city, this.zipCode].filter(Boolean).join(', '),
        'Service: ' + this.services
      ];
      if (this.message) lines.push('Details: ' + this.message);
      return 'mailto:homesroofingco@gmail.com?subject=' + encodeURIComponent('Free quote request - ' + (this.name || 'website'))
        + '&body=' + encodeURIComponent(lines.join('\n'));
    }
  }
}
</script>

<style scoped>
.hp-field{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden}
.buttonList {
  display: flex;
  flex-direction: row;
}

.buttonList.buttonList--hero {
  width: 100%;
  max-width: 860px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  margin: 0 auto;
}

.buttonList.buttonList--hero .contact-button,
.buttonList.buttonList--hero .financing-button {
  position: relative;
  overflow: hidden;
  width: 100%;
  margin-right: 0;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-radius: 10px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.34), 0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
}

.buttonList.buttonList--hero .contact-button {
  background: linear-gradient(135deg, #8d0808 0%, #bf1414 55%, #e04a2d 100%);
  font-size: 1.08rem;
  padding: 16px 22px;
  line-height: 1.2;
  text-transform: none;
}

.hero-quote-button {
  text-transform: none !important;
}

.buttonList.buttonList--hero .financing-button {
  background: linear-gradient(135deg, #1656a8 0%, #2b7de5 60%, #69aef8 100%);
  font-size: 1.12rem;
  padding: 16px 24px;
  animation-delay: 0.2s;
}

.hero-financing-button {
  text-transform: none !important;
}

@media (min-width: 1025px) {
  .buttonList.buttonList--hero {
    width: 100%;
    max-width: 1120px;
    flex-direction: row;
    align-items: stretch;
    gap: 14px;
  }

  .buttonList.buttonList--hero .contact-button,
  .buttonList.buttonList--hero .financing-button {
    flex: 1 1 0;
    width: auto;
    padding: 16px 20px;
    font-size: 1.02rem;
    border-radius: 8px;
    box-shadow: 0 7px 16px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  }

  .buttonList.buttonList--hero .contact-button::before,
  .buttonList.buttonList--hero .financing-button::before {
    display: none;
  }
}

@media (hover: hover) and (pointer: fine) {
  .buttonList.buttonList--hero .contact-button:hover,
  .buttonList.buttonList--hero .financing-button:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(255, 255, 255, 0.14) inset;
    filter: saturate(1.1) brightness(1.07);
  }

  .buttonList.buttonList--hero .contact-button:hover::before,
  .buttonList.buttonList--hero .financing-button:hover::before {
    left: 130%;
  }
}

@media (min-width: 1025px) and (hover: hover) and (pointer: fine) {
  .buttonList.buttonList--hero .contact-button:hover,
  .buttonList.buttonList--hero .financing-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    filter: brightness(1.03);
  }
}

.buttonList.buttonList--hero .contact-button:active,
.buttonList.buttonList--hero .financing-button:active {
  transform: translateY(1px) scale(0.995);
  filter: brightness(0.98);
}

.buttonList.buttonList--hero .contact-button:focus-visible,
.buttonList.buttonList--hero .financing-button:focus-visible {
  outline: 3px solid #f1c40f;
  outline-offset: 2px;
}

.buttonList.buttonList--hero .contact-button::before,
.buttonList.buttonList--hero .financing-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -130%;
  width: 50%;
  height: 100%;
  transform: skewX(-22deg);
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.35) 50%, rgba(255, 255, 255, 0) 100%);
  transition: left 0.45s ease;
}

.buttonList.buttonList--hero .contact-button:active::before,
.buttonList.buttonList--hero .financing-button:active::before {
  left: 130%;
}

/* Stack CTA buttons and make them near full-width on tablet and smaller */
@media (max-width: 1024px) {
  .buttonList {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    width: 92vw;
    max-width: 100%;
    margin: 0 auto;
  }
  
  .contact-button,
  .financing-button {
    width: 100%;
    min-width: 0;
    margin-right: 0;
    padding: 16px 18px;
  }

  .buttonList.buttonList--hero .contact-button,
  .buttonList.buttonList--hero .financing-button {
    animation: none;
  }

  .buttonList.buttonList--hero .contact-button {
    font-size: 1rem;
    padding: 15px 16px;
  }
}

/* Contact button */
.contact-button {
  background: linear-gradient(135deg, #8d0808 0%, #bf1414 55%, #e04a2d 100%);
  color: white;
  padding: 15px 30px; /* Increase padding */
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease; /* Add a transform transition */
  font-size: 1.2em; /* Increase font size */
  z-index: 2;
  margin-right: 10px;
  }

  @media (max-width: 500px){
    .contact-button {
      font-size: .9em;
    }
   }

  .financing-button{
    background: linear-gradient(135deg, #1656a8 0%, #2b7de5 60%, #69aef8 100%);
  color: white;
  padding: 15px 30px; /* Increase padding */
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease; /* Add a transform transition */
  font-size: 1.2em; /* Increase font size */
  z-index: 2;
  margin-right: 10px;
  }
  @media (max-width: 500px){
    .financing-button {
      font-size: .9em;
    }
   }

  .financing-button:hover {
  background-color: #5e9ae4;
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.3);
}

.financing-button:active {
  transform: translateY(2px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.5);
}


.contact-button:hover {
  background-color: #b11313;
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.3);
}

.contact-button:active {
  transform: translateY(2px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.5);
}

/* Modal */
.modal {
  display: none;
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 24px 16px;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  z-index: 1000;
}

.modal-open {
  overflow: hidden;

  @media screen and (max-height: 768px) {
    overflow: scroll;
  }
}

.modal-content {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 12px);
  padding-top: 36px;
  overflow-y: auto;
  background: linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%);
  border-radius: 20px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.6);
}

/* Form */
.form-data-collection {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  width: 100%;
  align-items: center;
  background: linear-gradient(135deg, #d0d4e4 0%, #b8bfd9 100%);
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  padding-top: 0;
}

.form-groups {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 15px;
  padding: 0 8px;
}

.form-group {
  position: relative;
  display: flex;
  flex: 1 1 calc(50% - 12px);
  min-width: 150px;
}

.form-groups input {
  height: 2.3rem;
  width: 100%;
  border: 2px solid #b0b8d4;
  border-radius: 5px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  background-color: white;
}

.form-groups input:focus {
  height: 2.3rem;
  border: 2px solid #6FACF9;
  box-shadow: 0 0 8px rgba(111, 172, 249, 0.3);
  outline: none;
}

.Selections {
  margin-bottom: 12px;
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  width: calc(100% - 16px);
}

.form-select {
  height: 2.3rem;
  border: 2px solid #b0b8d4;
  width: 100%;
  border-radius: 5px;
  margin-bottom: 10px;
  padding: 0 10px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  background-color: white;
}

.form-select:focus, .form-textarea:focus {
  border: 2px solid #6FACF9;
  box-shadow: 0 0 8px rgba(111, 172, 249, 0.3);
  outline: none;
}

.form-group input {
  padding: 8px 8px 3px 8px;
  border-radius: 5px;
  background-color: white;
}

.form-group input:focus ~ .floating-label,
.form-group input:not(:placeholder-shown) ~ .floating-label {
  font-size: 11px;
  opacity: 1;
  color: #6FACF9;
  transform: translate(-8px, -22px);
  font-weight: 600;
}

.form-group .floating-label {
  position: absolute;
  color: #666;
  pointer-events: none;
  left: 12px;
  top: 12px;
  transition: 0.2s ease all;
  background-color: white;
  padding: 0 4px;
}

.form-textarea {
  width: 100%;
  min-height: 76px;
  border: 2px solid #b0b8d4;
  border-radius: 5px;
  margin-bottom: 10px;
  padding: 8px 10px;
  font: inherit;
  font-size: 15px;
  resize: vertical;
  background-color: white;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.form-textarea::placeholder {
  color: #7a808c;
}


.selector-label .optional {
  font-weight: 400;
  color: #5b6270;
}

/* Thank-you screen */
.thanks {
  padding: 18px 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Roboto', sans-serif;
  color: #1f2a36;
}
.thanks-head {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.thanks-check {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1f7a45;
  color: #fff;
  box-shadow: 0 6px 16px rgba(31, 122, 69, 0.35);
}
.thanks-head h2 {
  margin: 4px 0 0;
  font-size: 22px;
  line-height: 1.25;
  color: #002655;
  text-wrap: balance;
}
.thanks-lead {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  max-width: 34em;
}
.thanks-note {
  margin: 0;
  font-size: 14px;
  color: #3d4757;
}
.thanks-tip {
  background: #fff4e0;
  border-left: 4px solid #d48a00;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 15px;
  line-height: 1.5;
}
.thanks-tip a,
.thanks-fix a {
  color: #8d0808;
  font-weight: 700;
}
.thanks-card,
.thanks-tip {
  text-align: left;
}
.thanks-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 2px 8px rgba(0, 38, 85, 0.08);
}
.thanks-card h3 {
  margin: 0 0 8px;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #4a5160;
}
.thanks-summary {
  margin: 0;
  display: grid;
  gap: 6px;
}
.thanks-summary div {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 8px;
  font-size: 15px;
}
.thanks-summary dt {
  color: #5b6270;
}
.thanks-summary dd {
  margin: 0;
  overflow-wrap: anywhere;
}
.thanks-details {
  white-space: pre-wrap;
}
.thanks-fix {
  margin: 10px 0 0;
  font-size: 13.5px;
  color: #4a5160;
}
.thanks-steps {
  margin: 0;
  padding-left: 20px;
  display: grid;
  gap: 6px;
  font-size: 15px;
  line-height: 1.45;
}
.thanks-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.thanks-btn {
  flex: 1 1 160px;
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border-radius: 10px;
  border: 2px solid #002655;
  background: #fff;
  color: #002655;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  font-family: inherit;
}
.thanks-btn:hover {
  background: #eef2f8;
}
.thanks-btn--primary {
  flex-basis: 100%;
  border-color: transparent;
  background: linear-gradient(135deg, #8d0808 0%, #bf1414 55%, #e04a2d 100%);
  color: #fff;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.25);
}
.thanks-btn--primary:hover {
  background: linear-gradient(135deg, #a01010 0%, #d01515 100%);
}
.thanks-close {
  align-self: center;
  background: none;
  border: 0;
  color: #002655;
  font-size: 15px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 6px 12px;
}

/* Close button */
.close {
  position: absolute;
  color: #950d0f;
  top: 8px;
  right: 12px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-size: 34px;
  font-weight: bold;
  cursor: pointer;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  z-index: 5;
}

/* Request button */
.Request {
  width: calc(100% - 16px);
  min-height: 48px;
  background: linear-gradient(135deg, #8d0808 0%, #bf1414 55%, #e04a2d 100%);
  color: white;
  display: flex;
  font-size: 18px;
  font-weight: 600;
  justify-content: center;
  align-items: center;
  margin: 8px;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.34), 0 0 0 1px rgba(255, 255, 255, 0.08) inset;
}

.Request:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, #a01010 0%, #d01515 100%);
}

.Request:disabled {
  opacity: 0.7;
  cursor: wait;
  transform: none;
}

.Request:active {
  transform: translateY(0px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Selector label */
.selector-label {
  display: block;
  text-wrap: balance;
  font-size: 16px;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15);
  color: #2c3e50;
  margin-bottom: 6px;
  margin-top: 5px;
  font-weight: 600;
}

/* Contact info */
.bottom-content {
  padding: 8px 8px 6px;
  color: white;
  background: linear-gradient(135deg, #002655 0%, #4a7ba7 100%);
  border-bottom-left-radius: 17px;
  border-bottom-right-radius: 17px;
  font-family: 'Roboto', sans-serif;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.3);
}
.contact-us-title h2 {
  font-size: 24px;
  margin: 6px 44px 10px;
  line-height: 1.2;
}
.contact-us-title{
  text-align: center;
  color: #2c3e50;
  font-size: 18px;
  font-family: 'Roboto', sans-serif;
  font-weight: bold;
  margin-top: 12px;
  margin-bottom: 2px;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
}

.error-fallback {
  background-color: #2c3e50;
  border: 2px solid #8d0808;
  border-radius: 8px;
  padding: 8px 10px;
  margin: 8px 10px;
  font-size: 16px;
  line-height: 1.6;
  color: white;
}

.error-fallback a {
  color: #f1c40f;
  font-weight: bold;
  text-decoration: none;
}

.error-fallback a:hover {
  text-decoration: underline;
}

.contact-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  padding: 0px;
  list-style-type: none;
}

.contact-info a {
  color: #f1c40f;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.3s ease;
}

.contact-info a:hover {
  color: white;
  text-decoration: underline;
}

.contact-info p {
  margin-top: 0px;
  font-size: 16px;
}

.contact-info h2 {
  margin: unset;
}

.contact-title {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: white;
  text-align: center;
}

@media (max-width: 768px) {
  .modal-content {
    padding-top: 8px;
  }

  .close {
    position: sticky;
    top: max(12px, env(safe-area-inset-top));
    right: auto;
    margin-left: auto;
    margin-right: 8px;
    z-index: 6;
  }
}

/* Mobile responsiveness */
@media (max-width: 500px) {
  .modal {
    padding: 12px 8px;
  }

  .modal-content {
    max-width: 96vw;
    max-height: calc(100vh - 8px);
    border-radius: 15px;
  }

  .close {
    margin-right: 6px;
    font-size: 32px;
  }
  
  .form-group {
    flex: 1 1 100%;
  }
  
  .form-groups {
    padding: 0 6px;
  }
  
  .Selections {
    padding: 0 6px;
  }
  
  .contact-us-title {
    font-size: 17px;
    margin-top: 12px;
  }
  
  .selector-label {
    font-size: 16px;
  }
  
  .Request {
    font-size: 17px;
    margin: 8px 6px;
  }
}

/* Option */

</style>